import { useCallback, useEffect, useRef, useState } from 'react'

type Props = {
  photoUrl: string | null
  onPhotoChange: (url: string | null) => void
  staffName: string
}

export function SelfieCapture({ photoUrl, onPhotoChange, staffName }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  /** Naik setiap stop — batalkan getUserMedia yang masih in-flight. */
  const cameraGenerationRef = useRef(0)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [useFallback, setUseFallback] = useState(false)
  const [isStarting, setIsStarting] = useState(true)

  const stopCamera = useCallback(() => {
    cameraGenerationRef.current += 1

    const stream = streamRef.current
    streamRef.current = null
    stream?.getTracks().forEach((track) => track.stop())

    const video = videoRef.current
    if (video) {
      const attached = video.srcObject
      if (attached instanceof MediaStream && attached !== stream) {
        attached.getTracks().forEach((track) => track.stop())
      }
      video.pause()
      video.srcObject = null
    }
  }, [])

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Kamera tidak didukung di perangkat ini.')
      setUseFallback(true)
      setIsStarting(false)
      return
    }

    const generation = cameraGenerationRef.current
    setIsStarting(true)
    setCameraError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      })

      if (generation !== cameraGenerationRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      streamRef.current = stream
      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        await video.play()
      }

      if (generation !== cameraGenerationRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        streamRef.current = null
        return
      }

      setUseFallback(false)
    } catch {
      if (generation !== cameraGenerationRef.current) return
      setCameraError(
        'Akses kamera ditolak. Izinkan kamera di pengaturan browser, atau gunakan unggah foto.',
      )
      setUseFallback(true)
    } finally {
      if (generation === cameraGenerationRef.current) {
        setIsStarting(false)
      }
    }
  }, [])

  useEffect(() => {
    if (photoUrl || useFallback) return undefined
    void startCamera()
    return () => stopCamera()
  }, [photoUrl, useFallback, startCamera, stopCamera])

  function captureFromCamera() {
    const video = videoRef.current
    if (!video || video.videoWidth === 0) return

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.drawImage(video, 0, 0)
    stopCamera()
    canvas.toBlob(
      (blob) => {
        if (!blob) return
        onPhotoChange(URL.createObjectURL(blob))
      },
      'image/jpeg',
      0.92,
    )
  }

  function handleFallbackFile(files: FileList | null) {
    if (!files?.[0]) return
    stopCamera()
    onPhotoChange(URL.createObjectURL(files[0]))
    setCameraError(null)
  }

  function retake() {
    if (photoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(photoUrl)
    }
    onPhotoChange(null)
    setUseFallback(false)
  }
  if (photoUrl) {
    return (
      <div className="space-y-3">
        <div className="aspect-[4/3] rounded-xl border-2 border-primary-container overflow-hidden bg-inverse-surface">
          <img src={photoUrl} alt={`Selfie ${staffName}`} className="w-full h-full object-cover" />
        </div>
        <button type="button" className="btn-outline w-full min-h-[44px]" onClick={retake}>
          Ambil ulang selfie
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {!useFallback && (
        <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-primary-container overflow-hidden bg-inverse-surface relative">
          {isStarting && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-body-sm">
              Membuka kamera…
            </div>
          )}
          <video
            ref={videoRef}
            playsInline
            muted
            className="w-full h-full object-cover -scale-x-100"
            aria-label={`Preview kamera selfie ${staffName}`}
          />
          {!isStarting && (
            <div className="absolute bottom-3 left-0 right-0 text-center">
              <p className="text-label-md text-white bg-inverse-surface/60 inline-block px-3 py-1 rounded-full">
                Wajah harus terlihat jelas
              </p>
            </div>
          )}
        </div>
      )}

      {useFallback && (
        <div className="aspect-[4/3] rounded-xl border-2 border-dashed border-outline-variant overflow-hidden bg-surface-container flex flex-col items-center justify-center p-4 text-center gap-2">
          <span className="material-symbols-outlined text-5xl text-on-surface-variant">
            photo_camera_front
          </span>
          <p className="text-body-sm font-semibold text-on-surface">Kamera tidak tersedia</p>
          <p className="text-label-md text-on-surface-variant">Gunakan unggah foto sebagai cadangan</p>
        </div>
      )}

      {cameraError && (
        <div role="alert" className="rounded-lg border border-status-warning/40 bg-status-warning-bg/40 px-3 py-2 text-body-sm text-status-warning">
          {cameraError}
        </div>
      )}

      {!useFallback ? (
        <button
          type="button"
          className="btn-primary w-full min-h-[48px] text-body-lg disabled:opacity-50"
          disabled={isStarting}
          onClick={captureFromCamera}
        >
          Ambil foto
        </button>
      ) : (
        <label className="btn-primary w-full min-h-[48px] inline-flex items-center justify-center gap-2 cursor-pointer">
          <span className="material-symbols-outlined">upload</span>
          Unggah foto selfie
          <input
            type="file"
            accept="image/*"
            capture="user"
            className="sr-only"
            onChange={(e) => handleFallbackFile(e.target.files)}
          />
        </label>
      )}

      {useFallback && (
        <button type="button" className="btn-outline w-full min-h-[44px]" onClick={() => void startCamera()}>
          Coba buka kamera lagi
        </button>
      )}
    </div>
  )
}
